import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../api";

const ERRORS = {
  WRONG_CODE: "That code isn't right. Check your app and try again.",
  TOO_MANY_ATTEMPTS: "Too many wrong tries. Wait 5 minutes and try again.",
  SET_PASSWORD_FIRST: "Set a password first.",
  NO_PENDING: "Setup expired. Please start again.",
};

const input = {
  width: "100%", boxSizing: "border-box", background: "#141414", border: "1px solid #222",
  borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none",
  marginBottom: 10, fontFamily: "inherit",
};

export function BackupCodes({ codes, accent = "#38bdf8" }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`RBXWar backup codes (each works once)\n\n${text}\n`], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url; a.download = "rbxwar-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  const small = { flex: 1, padding: "9px 0", background: "#141414", border: "1px solid #2a2a2a", borderRadius: 8, color: accent, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "#0a0a0a", border: "1px solid #222", borderRadius: 8, padding: 14, marginBottom: 10 }}>
        {codes.map(c => <div key={c} style={{ fontFamily: "monospace", fontSize: 15, letterSpacing: 1, color: "#fff", textAlign: "center" }}>{c}</div>)}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={small} onClick={copy}>{copied ? "Copied" : "Copy"}</button>
        <button style={small} onClick={download}>Download</button>
      </div>
    </div>
  );
}

export default function TwoFactorSetup({ token, accent = "#38bdf8", hasPassword = false, onDone }) {
  const [stage, setStage] = useState(hasPassword ? "loading" : "password"); // password | loading | scan | backup
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const fail = (e) => { if (!e.sessionExpired) setError(ERRORS[e.message] || e.message || "Something went wrong. Try again."); };

  const begin = async () => {
    setError("");
    try {
      const r = await api.twoFaStart(token);
      setSecret(r.secret); setUri(r.uri); setStage("scan");
    } catch (e) {
      if (e.message === "ALREADY_ENABLED") { onDone && onDone(); return; }
      fail(e);
      setStage("password");
    }
  };

  useEffect(() => { if (hasPassword) begin(); }, []);

  const savePassword = async () => {
    setError("");
    if (pw.length < 10) return setError("Use at least 10 characters.");
    if (pw !== pw2) return setError("Passwords don't match.");
    setBusy(true);
    try { await api.setPassword(token, pw); await begin(); } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const verify = async () => {
    setError("");
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your app.");
    setBusy(true);
    try {
      const r = await api.twoFaVerify(token, code);
      setCodes(r.backup_codes || []); setStage("backup");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const btn = { width: "100%", padding: "13px 0", border: "none", borderRadius: 8, background: accent, color: "#000", fontSize: 14.5, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "inherit", marginTop: 4, opacity: busy ? 0.7 : 1 };
  const err = error && <div style={{ color: "#f87171", fontSize: 12.5, marginBottom: 10, textAlign: "left" }}>{error}</div>;
  const total = hasPassword ? 2 : 3;
  const step = (n, text) => <div style={{ color: "#777", fontSize: 11.5, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12, textAlign: "left" }}>Step {hasPassword ? n - 1 : n} of {total}: {text}</div>;

  if (stage === "loading") return <div style={{ color: "#888", fontSize: 13, padding: "20px 0" }}>Setting up...</div>;

  if (stage === "password") return (
    <div style={{ textAlign: "left" }}>
      {step(1, "Choose a password")}
      <input style={input} type="password" placeholder="Password (min 10 characters)" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} autoFocus />
      <input style={input} type="password" placeholder="Confirm password" autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === "Enter" && savePassword()} />
      {err}
      <button style={btn} onClick={savePassword} disabled={busy}>{busy ? "Saving..." : "Continue"}</button>
    </div>
  );

  if (stage === "scan") return (
    <div style={{ textAlign: "left" }}>
      {step(2, "Scan with your authenticator app")}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <div style={{ background: "#fff", padding: 10, borderRadius: 10, lineHeight: 0 }}>
          <QRCodeSVG value={uri} size={168} level="M" />
        </div>
      </div>
      <div style={{ color: "#888", fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>
        Use Google Authenticator, Authy, 1Password or similar. Can't scan? Enter this key by hand:
      </div>
      <div style={{ fontFamily: "monospace", fontSize: 14, letterSpacing: 1.5, color: "#fff", background: "#0a0a0a", border: "1px solid #222", borderRadius: 8, padding: "10px 12px", marginBottom: 14, wordBreak: "break-all", textAlign: "center", userSelect: "all" }}>
        {secret.match(/.{1,4}/g)?.join(" ")}
      </div>
      <input
        style={{ ...input, textAlign: "center", fontSize: 22, letterSpacing: 8, fontWeight: 700 }}
        inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={e => e.key === "Enter" && verify()}
      />
      {err}
      <button style={btn} onClick={verify} disabled={busy}>{busy ? "Checking..." : "Turn on 2FA"}</button>
    </div>
  );

  return (
    <div style={{ textAlign: "left" }}>
      {step(3, "Save your backup codes")}
      <div style={{ color: "#aaa", fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
        If you lose your phone, each of these codes works <b style={{ color: "#fff" }}>once</b> in place of an app code. They are shown only now.
      </div>
      <BackupCodes codes={codes} accent={accent} />
      <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#bbb", fontSize: 13, margin: "14px 0 6px", cursor: "pointer" }}>
        <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} />
        I've saved my backup codes somewhere safe
      </label>
      <button style={{ ...btn, opacity: saved ? 1 : 0.4, cursor: saved ? "pointer" : "default" }} disabled={!saved} onClick={() => onDone && onDone()}>Finish</button>
    </div>
  );
}
